import { useEffect, useRef, useState } from "react"
import equal from "fast-deep-equal"
import type { MessageContent } from "@core/components/telegram/types"
import { validateMessageContent } from "@core/components/telegram/markup/validation"
import type { TemplateVersion, VersionContent } from "../interface/publications"
import {
  useCreateTemplateMutation,
  useCreateTemplateVersionMutation,
} from "../services/publications/templatesApi"
import { readPostBotError } from "../utils/apiErrors"

const isUuid = (id: unknown): id is string =>
  typeof id === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)

export interface TemplateWriteAccess {
  readOnly?: boolean
  canWrite?: boolean
}

/** Explicit source adapters; never serialize preview text or binding values. */
export function fromVersionContent(content: VersionContent): MessageContent {
  return structuredClone({
    text: content.text,
    mediaIds: content.mediaIds,
    buttons: content.buttons,
    formatting:
      content.parseMode !== undefined
        ? { kind: "markup", parseMode: content.parseMode }
        : content.entities !== undefined
          ? { kind: "entities", entities: content.entities }
          : { kind: "plain" },
  } as MessageContent)
}

export function toVersionContent(content: MessageContent): VersionContent {
  const common = {
    text: content.text,
    mediaIds: content.mediaIds,
    buttons: content.buttons,
  }
  return structuredClone(
    content.formatting.kind === "markup"
      ? { ...common, parseMode: content.formatting.parseMode }
      : content.formatting.kind === "entities"
        ? // Core also understands newer Telegram native annotations. The wire stores
          // them losslessly; the installed typegram declaration lags those types.
          {
            ...common,
            entities: content.formatting.entities as NonNullable<
              VersionContent["entities"]
            >,
          }
        : common,
  )
}

export interface VersionDraftOptions extends TemplateWriteAccess {
  /** New authoring session identity. Same-key refetches never reset local edits. */
  draftKey: string
  templateId?: string
  sourceVersion?: TemplateVersion
}
type UncertainStage = "template" | "version"
function initialize(options: VersionDraftOptions) {
  const content = options.sourceVersion
    ? fromVersionContent(options.sourceVersion.content)
    : fromVersionContent({ text: "", entities: [], mediaIds: [], buttons: [] })
  return {
    key: options.draftKey,
    session: Symbol(options.draftKey),
    name: "",
    content,
    initial: content,
    templateId: options.sourceVersion?.templateId ?? options.templateId,
    savedVersionId: undefined as string | undefined,
    lastGoodContent: validateMessageContent(content).valid ? content : null,
    saving: false,
    error: undefined as unknown,
    validationError: "",
    uncertain: undefined as UncertainStage | undefined,
  }
}

/** Two independent creates, not a transaction and not a revision/PATCH flow.
 * A lost/invalid response or 5xx may follow a write: only explicit reconciliation
 * or acknowledgement can unlock a new request. Confirmed IDs are never discarded.
 */
export function useVersionDraft(options: VersionDraftOptions) {
  const [createTemplate] = useCreateTemplateMutation()
  const [createVersion] = useCreateTemplateVersionMutation()
  const [stored, setState] = useState(() => initialize(options))
  const state = stored.key === options.draftKey ? stored : initialize(options)
  if (state !== stored) setState(state)
  const mounted = useRef(false)
  const inFlight = useRef<symbol | null>(null)
  const currentSession = useRef(state.session)
  currentSession.current = state.session
  const writable = !options.readOnly && options.canWrite !== false
  const access = useRef(writable)
  access.current = writable
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const editable = writable && !state.saving && !state.savedVersionId
  const validity = validateMessageContent(state.content)

  const submit = async (acknowledge = false): Promise<void> => {
    if (
      !editable ||
      inFlight.current === state.session ||
      (state.uncertain && !acknowledge)
    )
      return
    // Always revalidate authoritative source, including Enter/direct form submits.
    const valid = validateMessageContent(state.content)
    const nameError =
      !state.templateId && (state.name.length < 1 || state.name.length > 200)
    if (!valid.valid || nameError) {
      setState((previous) => ({
        ...previous,
        validationError: nameError
          ? "Название должно содержать от 1 до 200 символов"
          : "Исправьте исходное содержимое перед сохранением",
      }))
      return
    }
    // A key may be left and reopened while an old request is still pending.
    // Match the authoring session, not just its resource ID.
    const session = state.session
    const alive = () => mounted.current && currentSession.current === session
    inFlight.current = session
    setState((previous) => ({
      ...previous,
      saving: true,
      uncertain: undefined,
      error: undefined,
      validationError: "",
    }))
    let stage: UncertainStage = state.templateId ? "version" : "template"
    try {
      let templateId = state.templateId
      if (!templateId) {
        const template = await createTemplate({ name: state.name }).unwrap()
        if (!alive()) return
        if (!isUuid(template?.id))
          throw new Error("Ответ не содержит подтверждённый ID шаблона")
        templateId = template.id
        setState((previous) => ({ ...previous, templateId }))
      }
      if (!alive() || !access.current) return
      stage = "version"
      const saved = await createVersion({
        templateId,
        body: { content: toVersionContent(state.content) },
      }).unwrap()
      if (!alive()) return
      if (!isUuid(saved?.id) || saved.templateId !== templateId)
        throw new Error("Ответ не содержит подтверждённую версию этого шаблона")
      setState((previous) => ({ ...previous, savedVersionId: saved.id }))
    } catch (error) {
      if (!alive()) return
      const status = readPostBotError(error).status
      const rejected =
        typeof status === "number" &&
        status >= 400 &&
        status < 500 &&
        status !== 408
      setState((previous) => ({
        ...previous,
        error,
        uncertain: rejected ? undefined : stage,
      }))
    } finally {
      if (inFlight.current === session) inFlight.current = null
      if (alive()) setState((previous) => ({ ...previous, saving: false }))
    }
  }

  return {
    ...state,
    validity,
    editable,
    dirty:
      !state.savedVersionId &&
      (state.saving ||
        !!state.uncertain ||
        !!state.name ||
        !equal(state.content, state.initial) ||
        (!options.templateId && !options.sourceVersion && !!state.templateId)),
    setName: (name: string) => {
      if (editable && !state.templateId && !state.uncertain)
        setState((previous) => ({ ...previous, name }))
    },
    setContent: (content: MessageContent) => {
      if (editable)
        setState((previous) => ({
          ...previous,
          content,
          lastGoodContent: validateMessageContent(content).valid
            ? content
            : previous.lastGoodContent,
          validationError: "",
        }))
    },
    submit: () => submit(),
    /** A deliberate retry acknowledges the warning and submits the same snapshot. */
    retry: () => (state.uncertain ? submit(true) : Promise.resolve()),
    acknowledgeUncertain: () => {
      if (editable)
        setState((previous) => ({
          ...previous,
          uncertain: undefined,
          error: undefined,
        }))
    },
    /** Only after explicitly selecting a confirmed template from the library. */
    useExistingTemplate: (templateId: string) => {
      if (editable && state.uncertain === "template" && isUuid(templateId))
        setState((previous) => ({
          ...previous,
          templateId,
          uncertain: undefined,
          error: undefined,
        }))
    },
  }
}
